import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { searchApps, searchAppsWithYoogaCustomerId } from 'services/app';
import { listContractsByParams } from 'services/contract';
import { getContractTypesCached } from 'services/contractType.cached';
import { getCustomer } from 'services/customer';
import { SearchResponse } from 'services/interfaces/search';
import { search } from 'services/search';
import {
  getFreemiumContractTypeIds,
  hasFreemiumContract,
} from './tabs/Financial/helpers/contractType';
import { onlyNumbers } from 'shared/helpers/string';
import { App } from 'shared/models/app';
import { Customer } from 'shared/models/customer';
import { default as CustomerPage } from './Customer';
import CreateInstallationModal from './components/CreateInstallationModal';
import EditCustomerModal from './components/EditCustomerModal';

interface CustomerContextData {
  customer: Customer;
  apps: App[];
  info: SearchResponse;
  loadCustomer(): void;
  loadApps(result?: SearchResponse): void;
  loading: boolean;
  isFreemium: boolean;
  setEditCustomerModalOpen(b: boolean): void;
  setInstallationModalOpen(b: boolean): void;
}

const Context = createContext<CustomerContextData>({} as CustomerContextData);

export const useCustomerContext = () => useContext(Context);

const CustomerContext = () => {
  const navigate = useNavigate();
  const { document } = useParams();
  const { state } = useLocation();
  const [info, setInfo] = useState<SearchResponse>({} as SearchResponse);
  const [customer, setCustomer] = useState<Customer>({} as Customer);
  const [apps, setApps] = useState<App[]>([]);
  const [isFreemium, setIsFreemium] = useState(false);

  const [editCustomerModalOpen, setEditCustomerModalOpen] = useState(false);

  const [installationModalOpen, setInstallationModalOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [appLoading, setAppLoading] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);

  const onCloseInstallationModal = () => {
    setInstallationModalOpen(false);
  };

  const loadError = useCallback(() => {
    toast.error('Não foi possível ver o cliente');
    navigate('/');
  }, [navigate]);

  const fetch = () => {
    if (!document) return loadError();

    if (state?.result) return postFetch(state.result);

    setLoading(true);
    search(document!).then(async (response) => {
      const docNorm = onlyNumbers(document!);
      const result = response.data.find(
        (r) => r.document === document || onlyNumbers(r.document) === docNorm,
      );

      if (!result) return loadError();

      await postFetch(result);
    });
  };

  const postFetch = async (result: SearchResponse) => {
    if (result.customer_id) {
      await loadCustomer(result.customer_id);
      void checkFreemium(result.customer_id);
    }

    setInfo(result);
  };

  // "Shot" disparado ao abrir o cliente: busca a lista de contratos + os tipos
  // (já cacheados) e cruza contract_type_id -> tipo freemium para exibir a badge
  // no topo, sem depender da aba Financeiro estar aberta.
  const checkFreemium = useCallback(async (customerId: number) => {
    try {
      const [contractsResponse, contractTypes] = await Promise.all([
        listContractsByParams({
          customer: customerId,
          limit: 100,
          offset: 0,
          includeDeleted: false,
        }),
        getContractTypesCached(),
      ]);

      const freemiumTypeIds = getFreemiumContractTypeIds(contractTypes);
      const contracts = contractsResponse.data.results ?? [];

      setIsFreemium(hasFreemiumContract(contracts, freemiumTypeIds));
    } catch {
      setIsFreemium(false);
    }
  }, []);

  const loadApps = useCallback(
    (result?: SearchResponse) => {
      if (!result?.document && !info.document) return;
      const doc = onlyNumbers(result?.document ?? info.document ?? document);

      setAppLoading(true);

      if (customer?.yoogaCustomerId) {
        searchAppsWithYoogaCustomerId(doc, customer.yoogaCustomerId)
          .then((response) => {
            const apps = response.data.results
              .filter(
                (appData) =>
                  onlyNumbers(appData.cnpj) === doc ||
                  appData.yooga_customer_id === customer.yoogaCustomerId,
              )
              .map((appData) => new App(appData));

            setApps(apps);
          })
          .catch(() => toast.error('Falha ao carregar apps'))
          .finally(() => setAppLoading(false));
      } else {
        searchApps(doc)
          .then((response) => {
            setApps(
              response.data.results
                .filter((appData) => onlyNumbers(appData.cnpj) === doc)
                .map((appData) => new App(appData)),
            );
          })
          .catch(() => toast.error('Falha ao carregar apps'))
          .finally(() => setAppLoading(false));
      }
    },
    [info, document, customer],
  );

  const loadCustomer = useCallback(
    (customerId?: number) => {
      return new Promise<void>((resolve, reject) => {
        if (!customer.id && !customerId) {
          resolve();
          return;
        }

        const id = customerId ?? customer.id;

        setCustomerLoading(true);

        getCustomer(id, true)
          .then((response) => {
            response.data.contract = { id: 1 } as any;
            setCustomer(new Customer(response.data));
            resolve();
          })
          .catch((error) => {
            loadError();
            reject(error);
          })
          .finally(() => setCustomerLoading(false));
      });
    },
    [customer, loadError],
  );

  useEffect(() => {
    if (info?.yooga_apps?.length > 0) {
      loadApps(info);
    }
  }, [customer, info, loadApps]);

  useEffect(() => {
    setLoading(appLoading || customerLoading);
  }, [appLoading, customerLoading]);

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = useMemo<CustomerContextData>(
    () => ({
      customer,
      apps,
      info,
      loadCustomer,
      loadApps,
      loading,
      isFreemium,
      setEditCustomerModalOpen,
      setInstallationModalOpen,
    }),
    [customer, loadCustomer, loading, apps, info, loadApps, isFreemium],
  );

  return (
    <Context.Provider value={data}>
      <EditCustomerModal
        isOpen={editCustomerModalOpen}
        onClose={() => setEditCustomerModalOpen(false)}
      />
      <CreateInstallationModal
        isOpen={installationModalOpen}
        onClose={onCloseInstallationModal}
      />
      <CustomerPage />
    </Context.Provider>
  );
};

export default CustomerContext;
